import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { TrackList } from 'components/library/TrackList';
import { ImportLibraryDialog } from 'components/library/dialog/ImportLibraryDialog';
import { useLocalDatabase } from 'hooks/useLocalDatabase';
import { useMusicLibraryImport } from 'hooks/useMusicLibraryImport';
import { LocalTrack } from 'models/LocalTrack';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

export function MusicLibrary() {
  const { t } = useTranslation();
  const [isStarted, setIsStarted] = useState(false);
  const [musicLibraryPath, setMusicLibraryPath] = useState<string[]>([]);
  const [localTracks, setLocalTracks] = useState<LocalTrack[]>([]);
  const [localTracksCount, setLocalTracksCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const {
    handleAbort,
    handleImport,
    importedCount,
    readCount,
    importing,
    resetDatabase,
    countFilesRecursive,
    failedFiles,
  } = useMusicLibraryImport();

  const { localDatabase, localDatabaseLoaded } = useLocalDatabase();

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Database management menu & dialog state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const [isImportConfirmOpen, setIsImportConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleLibraryImport = async () => {
    setIsStarted(true);
    musicLibraryPath.forEach(handleImport);
  };

  const handleAddDirectoryShell = () => {
    console.info('handleAddDirectoryShell');
    console.info(window.electron);
    window.electron?.selectDirectory().then(handleAddDirectory);
  };

  const handleAddDirectory = (newPath: string) => {
    if (newPath && !musicLibraryPath.includes(newPath)) {
      const newMusicLibraryPath = [...musicLibraryPath, newPath];
      setMusicLibraryPath(newMusicLibraryPath);
      localStorage.setItem('musicLibraryPath', JSON.stringify(newMusicLibraryPath));
    }
  };

  const handleRemoveDirectory = (removePath: string) => {
    const newMusicLibraryPath = musicLibraryPath.filter((value) => value != removePath);
    setMusicLibraryPath(newMusicLibraryPath);
    localStorage.setItem('musicLibraryPath', JSON.stringify(newMusicLibraryPath));
  };

  const handleResetDatabase = () => {
    resetDatabase();
    setSnackbarMessage('Database has been reset successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleBackupDb = async () => {
    handleCloseMenu();
    try {
      const success = await window.electron?.backupDatabase();
      if (success) {
        setSnackbarMessage(t('databaseBackupSuccess', 'Database backed up successfully!'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to backup database');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleImportDbClick = () => {
    handleCloseMenu();
    setIsImportConfirmOpen(true);
  };

  const handleImportDbConfirm = async () => {
    setIsImportConfirmOpen(false);
    try {
      const success = await window.electron?.importDatabase();
      if (success) {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to import database');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteDbClick = () => {
    handleCloseMenu();
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteDbConfirm = async () => {
    setIsDeleteConfirmOpen(false);
    try {
      const success = await window.electron?.deleteDatabase();
      if (success) {
        await localDatabase?.createTables();
        setLocalTracks([]);
        setLocalTracksCount(0);
        setSnackbarMessage(
          t('databaseDeleteSuccess', 'Database deleted and recreated successfully!')
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage('Failed to delete database');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenPath = (path: string) => {
    window.electron?.openPath(path);
  };

  useEffect(() => {
    localStorage.setItem('importedCount', importedCount.toString());
  }, [importedCount]);

  useEffect(() => {
    localStorage.setItem('readCount', readCount.toString());
  }, [readCount]);

  useEffect(() => {
    sessionStorage.setItem('importingMusicLibrary', importing ? 'true' : 'false');
  }, [importing]);

  useEffect(() => {
    if (!importing && localDatabaseLoaded && localDatabase) {
      // @ts-ignore
      window.resetDatabase = handleResetDatabase;

      const loadTracks = async () => {
        try {
          let tracks: LocalTrack[] = [];

          if (searchQuery.trim() !== '') {
            console.log(`🔍 Running vector search for: "${searchQuery}"`);
            tracks = await localDatabase.findTracks({
              query: searchQuery,
              maxDistance: 2.0,
              limit: 20,
            });

            // --- NEW: Log top 10 results with distance to console ---
            console.log(`\n🏆 Top 10 Search Results for "${searchQuery}":`);
            tracks.slice(0, 10).forEach((track, index) => {
              // Cast to any to access the dynamically appended 'distance' property from SQLite
              const distance = (track as any).distance;
              const formattedDistance =
                distance !== undefined ? Number(distance).toFixed(4) : 'N/A';

              console.log(
                `#${index + 1} | Distance: ${formattedDistance} | ${
                  track.artist || 'Unknown Artist'
                } - ${track.title || 'Unknown Title'}`
              );
            });
            console.log('--------------------------------------------------\n');
          } else {
            tracks = await localDatabase.getAllTracks();
          }

          if (tracks) {
            setLocalTracks(tracks);
          }
        } catch (error) {
          console.error('🔴 Failed to load/search tracks:', error);
        }
      };

      const timeoutId = setTimeout(() => {
        loadTracks();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [localDatabaseLoaded, importing, localDatabase, searchQuery]);

  useEffect(() => {
    Promise.all(musicLibraryPath.map((path) => countFilesRecursive(path) || 0)).then(
      (allCounts) => {
        setLocalTracksCount(allCounts.reduce((a, b) => a + b, 0));
      }
    );
  }, [musicLibraryPath]);

  useEffect(() => {
    const storedLibraryPath = localStorage.getItem('musicLibraryPath') || '';

    if (storedLibraryPath) {
      try {
        const json = JSON.parse(storedLibraryPath);
        setMusicLibraryPath(json);
      } catch (error) {
        setMusicLibraryPath([storedLibraryPath]);
      }
    }
  }, []);

  return (
    <>
      <ImportLibraryDialog
        open={importing || isStarted}
        importing={importing}
        failedFiles={failedFiles}
        onCancel={handleAbort}
        onClose={() => setIsStarted(false)}
        readCount={readCount}
        importedCount={importedCount}
        allFilesCount={localTracksCount}
      />
      <Typography gutterBottom variant="h1">
        {t('musicLibrary', 'Music Library')}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
          width: '100%',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          onClick={handleLibraryImport}
          disabled={importing || !musicLibraryPath.length}
          startIcon={<CheckIcon />}
        >
          {t('startImport', 'Start Import')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleAddDirectoryShell}
          startIcon={<AddIcon />}
        >
          {t('addDirectory', 'Add directory')}
        </Button>
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton
            id="db-menu-button"
            aria-controls={isMenuOpen ? 'db-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
            onClick={handleOpenMenu}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="db-menu"
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleCloseMenu}
            MenuListProps={{
              'aria-labelledby': 'db-menu-button',
            }}
          >
            <MenuItem onClick={handleBackupDb}>{t('backupDatabase', 'Back up database')}</MenuItem>
            <MenuItem onClick={handleImportDbClick}>
              {t('importDatabase', 'Import database')}
            </MenuItem>
            <MenuItem onClick={handleDeleteDbClick}>
              {t('deleteDatabase', 'Delete database')}
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
      <Alert severity="warning" sx={{ mb: 2 }}>
        {t(
          'addDirectoryDescription',
          'Depending on your library size and hard disk speed, the import could take a few minutes or\r\n        hours.'
        )}
      </Alert>
      {musicLibraryPath.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <List>
            {musicLibraryPath.map((path, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveDirectory(path)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <IconButton onClick={() => handleOpenPath(path)} sx={{ p: 0 }}>
                    <Avatar>
                      <FolderIcon />
                    </Avatar>
                  </IconButton>
                </ListItemAvatar>
                <ListItemText primary={path} />
              </ListItem>
            ))}
          </List>
        </Card>
      )}

      <Box sx={{ mt: 4, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search tracks by mood, genre, or semantic meaning..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Typography variant="h3" gutterBottom>
        {t('importedTracks', 'Imported Tracks')}
      </Typography>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <TrackList trackList={localTracks} />
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Database Import Confirmation Dialog */}
      <Dialog
        open={isImportConfirmOpen}
        onClose={() => setIsImportConfirmOpen(false)}
        aria-labelledby="import-db-dialog-title"
        aria-describedby="import-db-dialog-description"
      >
        <DialogTitle id="import-db-dialog-title">
          {t('importDatabaseTitle', 'Import database?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="import-db-dialog-description">
            {t(
              'importDatabaseConfirm',
              'Are you sure you want to select an existing database backup? This will overwrite the current database.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportConfirmOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={handleImportDbConfirm} color="primary" variant="contained">
            {t('importDatabase', 'Import database')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Database Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        aria-labelledby="delete-db-dialog-title"
        aria-describedby="delete-db-dialog-description"
      >
        <DialogTitle id="delete-db-dialog-title">
          {t('deleteDatabaseTitle', 'Delete database?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-db-dialog-description">
            {t(
              'deleteDatabaseConfirm',
              'Are you sure you want to delete the current database? All imported tracks and mappings will be lost. The database will be recreated empty.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={handleDeleteDbConfirm} color="error" variant="contained">
            {t('deleteDatabase', 'Delete database')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
